import { useEffect, useState } from "react";
import api, { errMsg, formatTL, formatNumber } from "@/lib/api";
import PageHeader from "@/components/app/PageHeader";
import ExportButtons from "@/components/app/ExportButtons";
import { toast } from "sonner";
import { TrendingUp, TrendingDown, DollarSign, Fuel, Wallet, Building2, Factory, Truck, GitCompare, X, Receipt } from "lucide-react";

const MONTHS = ["Oca","Şub","Mar","Nis","May","Haz","Tem","Ağu","Eyl","Eki","Kas","Ara"];

const TABS = [
  { key: "companies", label: "Firmalar", icon: Building2, testid: "tab-companies" },
  { key: "suppliers", label: "Tedarikçiler", icon: Factory, testid: "tab-suppliers" },
  { key: "drivers",   label: "Şoförler", icon: Truck, testid: "tab-drivers" },
  { key: "fuel",      label: "Yakıt", icon: Fuel, testid: "tab-fuel" },
  { key: "salaries",  label: "Maaşlar", icon: Wallet, testid: "tab-salaries" },
];

export default function YearlyConsolidatedPage() {
  const [year, setYear] = useState(new Date().getFullYear() - 1);
  const [compareYear, setCompareYear] = useState(null); // null = tekli mod
  const [data, setData] = useState(null);
  const [compData, setCompData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState("companies");
  const [search, setSearch] = useState("");

  // Firma detay modal state
  const [detail, setDetail] = useState(null);  // { row, data, loading }

  const openCompanyDetail = async (row) => {
    setDetail({ row, data: null, loading: true });
    try {
      const { data } = await api.get(`/reports/yearly-consolidated/company-detail?year=${year}&company_id=${encodeURIComponent(row.id)}`);
      setDetail({ row, data, loading: false });
    } catch (e) {
      toast.error(errMsg(e));
      setDetail(null);
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const primary = api.get(`/reports/yearly-consolidated?year=${year}`);
      const secondary = compareYear ? api.get(`/reports/yearly-consolidated?year=${compareYear}`) : Promise.resolve(null);
      const [p, s] = await Promise.all([primary, secondary]);
      setData(p.data);
      setCompData(s?.data || null);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [year, compareYear]);

  if (!data && !loading) return <div className="p-8 text-sm text-neutral-500">Yükleniyor...</div>;

  const g = data?.grand || {};
  const cg = compData?.grand || {};
  const monthly = data?.monthly || {};

  // KPI delta helper
  const delta = (a, b) => {
    if (!compData) return null;
    if (!b) return { pct: null, sign: 0 };
    const diff = a - b;
    const pct = b !== 0 ? (diff / Math.abs(b)) * 100 : null;
    return { diff, pct, sign: diff === 0 ? 0 : (diff > 0 ? 1 : -1) };
  };

  const rows = (data?.[tab] || []).filter(r => {
    if (!search.trim()) return true;
    const q = search.toLocaleLowerCase("tr-TR").replaceAll("ı","i").replaceAll("ş","s").replaceAll("ç","c").replaceAll("ğ","g").replaceAll("ü","u").replaceAll("ö","o");
    const hay = ((r.name || r.plate || "") + " " + (r.role || "") + " " + (r.brand || "")).toLocaleLowerCase("tr-TR")
      .replaceAll("ı","i").replaceAll("ş","s").replaceAll("ç","c").replaceAll("ğ","g").replaceAll("ü","u").replaceAll("ö","o");
    return hay.includes(q);
  });

  const kpis = [
    { label: "Kesilen Fatura", val: g.sales_total || 0, prev: cg.sales_total || 0, color: "text-[#0020C2]", icon: TrendingUp },
    { label: "Tahsilat", val: g.incoming_total || 0, prev: cg.incoming_total || 0, color: "text-[#16A34A]", icon: DollarSign },
    { label: "Araç Kazancı", val: g.vehicle_earnings_total || 0, prev: cg.vehicle_earnings_total || 0, color: "text-[#0020C2]", icon: Truck },
    { label: "Tedarikçi Gideri", val: g.supplier_invoice_total || 0, prev: cg.supplier_invoice_total || 0, color: "text-[#DC2626]", icon: Factory },
    { label: "Yakıt Gideri", val: g.fuel_total || 0, prev: cg.fuel_total || 0, color: "text-[#F59E0B]", icon: Fuel },
    { label: "Maaş Gideri", val: g.salary_total || 0, prev: cg.salary_total || 0, color: "text-[#F59E0B]", icon: Wallet },
    { label: "Ekstra Gider", val: g.extra_expense_total || 0, prev: cg.extra_expense_total || 0, color: "text-[#7C3AED]", icon: Receipt },
    { label: "Net Kar", val: g.net_profit || 0, prev: cg.net_profit || 0,
      color: (g.net_profit || 0) >= 0 ? "text-[#16A34A]" : "text-[#DC2626]",
      icon: (g.net_profit || 0) >= 0 ? TrendingUp : TrendingDown },
  ];

  // Export sections
  const exportSections = data ? [
    {
      name: "AylikOzet", heading: `${year} Yılı Aylık Özet`,
      columns: [
        { key: "kategori", label: "Kategori" },
        ...MONTHS.map((m, i) => ({ key: `m${i}`, label: m, format: "number" })),
        { key: "toplam", label: "Toplam", format: "TL" },
      ],
      rows: [
        { key: "sales", label: "Kesilen Fatura" },
        { key: "incoming", label: "Tahsilat" },
        { key: "earnings", label: "Araç Kazancı" },
        { key: "supplier_inv", label: "Tedarikçi Gideri" },
        { key: "fuel", label: "Yakıt Gideri" },
        { key: "salary", label: "Maaş Gideri" },
        { key: "extra_expense", label: "Ekstra Gider" },
      ].map(r => {
        const arr = monthly[r.key] || [];
        const total = arr.reduce((a,b) => a + (b || 0), 0);
        const row = { kategori: r.label, toplam: total };
        arr.forEach((v, i) => { row[`m${i}`] = v; });
        return row;
      })
    },
    {
      name: "Firmalar", heading: `${year} Yılı Firma Bazlı`,
      columns: [
        { key: "name", label: "Firma" },
        { key: "invoice_total", label: "Fatura", format: "TL" },
        { key: "payment_total", label: "Tahsilat", format: "TL" },
        { key: "uncollected", label: "Tahsil Edilmemiş", format: "TL_warn" },
        { key: "balance", label: "Bakiye", format: "TL" },
      ],
      rows: data.companies || [],
    },
    {
      name: "Tedarikciler", heading: `${year} Yılı Tedarikçi Bazlı`,
      columns: [
        { key: "name", label: "Tedarikçi" },
        { key: "invoice_total", label: "Fatura", format: "TL" },
        { key: "payment_total", label: "Ödeme", format: "TL" },
        { key: "balance", label: "Bakiye", format: "TL" },
      ],
      rows: data.suppliers || [],
    },
    {
      name: "Soforler", heading: `${year} Yılı Şoför Bazlı`,
      columns: [
        { key: "name", label: "Şoför" },
        { key: "earnings_total", label: "Araç Kazancı", format: "TL" },
        { key: "salary_total", label: "Maaş", format: "TL" },
        { key: "net", label: "Net", format: "TL" },
      ],
      rows: data.drivers || [],
    },
    {
      name: "Yakit", heading: `${year} Yılı Araç Bazlı Yakıt`,
      columns: [
        { key: "plate", label: "Plaka" },
        { key: "brand", label: "Marka" },
        { key: "model", label: "Model" },
        { key: "count", label: "Adet", format: "number" },
        { key: "total", label: "Toplam", format: "TL" },
      ],
      rows: data.fuel || [],
    },
    {
      name: "Maaslar", heading: `${year} Yılı Kişi Bazlı Maaş`,
      columns: [
        { key: "name", label: "Çalışan" },
        { key: "role", label: "Görev" },
        { key: "count", label: "Ay", format: "number" },
        { key: "total", label: "Toplam", format: "TL" },
      ],
      rows: data.salaries || [],
    },
  ] : [];

  return (
    <>
      <PageHeader
        title="Yıllık Konsolide Rapor"
        subtitle={`${year} yılı${compareYear ? ` vs ${compareYear}` : ""} — Firma, tedarikçi, şoför, yakıt ve maaş bazlı yıllık gösterim`}
        actions={
          <div className="flex items-center gap-2 flex-wrap">
            {data && (
              <ExportButtons
                filename={`yillik-konsolide-${year}${compareYear ? `-vs-${compareYear}` : ""}`}
                title={`Yıllık Konsolide Rapor — ${year}${compareYear ? ` vs ${compareYear}` : ""}`}
                subtitle="Firma, tedarikçi, şoför, yakıt ve maaş bazlı yıllık gösterim"
                sections={exportSections}
              />
            )}
            <label className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold">Yıl:</label>
            <select data-testid="year-select" value={year} onChange={e => setYear(Number(e.target.value))}
              className="px-3 py-2 border border-[#E5E5E5] text-sm num bg-white">
              {[2022, 2023, 2024, 2025, 2026].map(y => <option key={y} value={y}>{y}</option>)}
            </select>
            <button
              data-testid="toggle-compare"
              onClick={() => setCompareYear(compareYear ? null : (year - 1))}
              className={`flex items-center gap-1.5 px-3 py-2 text-xs font-semibold uppercase tracking-widest border ${
                compareYear ? "bg-[#0020C2] text-white border-[#0020C2]" : "border-[#E5E5E5] hover:bg-[#F5F5F5]"
              }`}
            >
              <GitCompare className="w-3.5 h-3.5" /> Karşılaştır
            </button>
            {compareYear && (
              <select data-testid="compare-year-select" value={compareYear} onChange={e => setCompareYear(Number(e.target.value))}
                className="px-3 py-2 border border-[#0020C2] text-sm num bg-[#F0F5FF]">
                {[2022, 2023, 2024, 2025, 2026].filter(y => y !== year).map(y => <option key={y} value={y}>{y}</option>)}
              </select>
            )}
          </div>
        }
      />

      {loading && <div className="text-center py-8 text-sm text-neutral-500">Yükleniyor...</div>}

      {!loading && data && (
        <>
          {/* KPI cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3 mb-6" data-testid="yearly-kpis">
            {kpis.map(k => {
              const Icon = k.icon;
              const d = delta(k.val, k.prev);
              return (
                <div key={k.label} className="border border-[#E5E5E5] p-3 bg-white">
                  <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-neutral-500">
                    <Icon className="w-3 h-3" /> {k.label}
                  </div>
                  <div className={`num font-bold text-sm mt-1 ${k.color}`}>{formatTL(k.val)}</div>
                  {d && (
                    <div className={`text-[10px] mt-0.5 num ${d.sign > 0 ? "text-[#16A34A]" : d.sign < 0 ? "text-[#DC2626]" : "text-neutral-500"}`}>
                      {d.sign > 0 ? "▲" : d.sign < 0 ? "▼" : "="} {d.pct !== null ? `${d.pct.toFixed(1)}%` : "-"} <span className="text-neutral-400">vs {compareYear}</span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Monthly summary chart (bar table) */}
          <div className="border border-[#E5E5E5] bg-white p-4 mb-6 overflow-x-auto" data-testid="monthly-summary">
            <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold mb-3">Aylık Özet</div>
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-[#E5E5E5]">
                  <th className="px-2 py-1.5 text-left text-[10px] uppercase text-neutral-500 font-semibold">Kategori</th>
                  {MONTHS.map(m => <th key={m} className="px-1.5 py-1.5 text-right text-[10px] uppercase text-neutral-500 font-semibold num">{m}</th>)}
                  <th className="px-2 py-1.5 text-right text-[10px] uppercase text-neutral-500 font-semibold">Toplam</th>
                </tr>
              </thead>
              <tbody>
                {[
                  { key: "sales", label: "Kesilen Fatura", color: "text-[#0020C2]" },
                  { key: "incoming", label: "Tahsilat", color: "text-[#16A34A]" },
                  { key: "earnings", label: "Araç Kazancı", color: "text-[#0020C2]" },
                  { key: "supplier_inv", label: "Tedarikçi Gideri", color: "text-[#DC2626]" },
                  { key: "fuel", label: "Yakıt Gideri", color: "text-[#F59E0B]" },
                  { key: "salary", label: "Maaş Gideri", color: "text-[#F59E0B]" },
                  { key: "extra_expense", label: "Ekstra Gider", color: "text-[#7C3AED]" },
                ].map(row => {
                  const arr = monthly[row.key] || [];
                  const total = arr.reduce((a,b) => a + (b || 0), 0);
                  return (
                    <tr key={row.key} className="border-b border-[#F5F5F5]">
                      <td className={`px-2 py-1.5 font-semibold ${row.color}`}>{row.label}</td>
                      {arr.map((v, i) => (
                        <td key={i} className="px-1.5 py-1.5 text-right num text-[10px] text-neutral-700">
                          {v > 0 ? formatNumber(v) : "-"}
                        </td>
                      ))}
                      <td className={`px-2 py-1.5 text-right num font-bold ${row.color}`}>{formatTL(total)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Tabs */}
          <div className="flex items-center gap-1 border-b border-[#E5E5E5] mb-4 overflow-x-auto">
            {TABS.map(t => {
              const Icon = t.icon;
              return (
                <button
                  key={t.key}
                  data-testid={t.testid}
                  onClick={() => { setTab(t.key); setSearch(""); }}
                  className={`flex items-center gap-2 px-4 py-2.5 text-xs uppercase tracking-widest font-semibold border-b-2 whitespace-nowrap transition-colors ${
                    tab === t.key ? "border-[#0A0A0A] text-[#0A0A0A]" : "border-transparent text-neutral-500 hover:text-[#0A0A0A]"
                  }`}
                >
                  <Icon className="w-3.5 h-3.5" /> {t.label}
                  <span className="text-[10px] text-neutral-400 num">({(data[t.key] || []).length})</span>
                </button>
              );
            })}
          </div>

          {/* Search */}
          <div className="mb-3">
            <input
              data-testid="yearly-search"
              type="text"
              placeholder={`Ara: ${TABS.find(t=>t.key===tab)?.label.toLowerCase()}...`}
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="w-full max-w-md px-3 py-2 text-sm border border-[#E5E5E5] focus:border-[#0A0A0A] focus:outline-none"
            />
          </div>

          {/* Table */}
          <div className="border border-[#E5E5E5] bg-white overflow-x-auto" data-testid={`table-${tab}`}>
            <table className="w-full text-xs">
              <thead className="bg-[#FAFAFA]">
                <tr>
                  <th className="px-3 py-2 text-left text-[10px] uppercase tracking-widest text-neutral-500 font-semibold">
                    {tab === "fuel" ? "Plaka" : tab === "salaries" ? "Çalışan" : "Ad"}
                  </th>
                  {tab === "companies" && (
                    <>
                      <th className="px-2 py-2 text-right text-[10px] uppercase text-neutral-500 font-semibold">Fatura</th>
                      <th className="px-2 py-2 text-right text-[10px] uppercase text-neutral-500 font-semibold">Tahsilat</th>
                      <th className="px-2 py-2 text-right text-[10px] uppercase text-[#DC2626] font-semibold">Tahsil Edilmemiş</th>
                      <th className="px-2 py-2 text-right text-[10px] uppercase text-neutral-500 font-semibold">Bakiye</th>
                    </>
                  )}
                  {tab === "suppliers" && (
                    <>
                      <th className="px-2 py-2 text-right text-[10px] uppercase text-neutral-500 font-semibold">Fatura</th>
                      <th className="px-2 py-2 text-right text-[10px] uppercase text-neutral-500 font-semibold">Ödeme</th>
                      <th className="px-2 py-2 text-right text-[10px] uppercase text-neutral-500 font-semibold">Bakiye</th>
                    </>
                  )}
                  {tab === "drivers" && (
                    <>
                      <th className="px-2 py-2 text-right text-[10px] uppercase text-neutral-500 font-semibold">Araç Kazancı</th>
                      <th className="px-2 py-2 text-right text-[10px] uppercase text-neutral-500 font-semibold">Maaş</th>
                      <th className="px-2 py-2 text-right text-[10px] uppercase text-neutral-500 font-semibold">Net</th>
                    </>
                  )}
                  {tab === "fuel" && (
                    <>
                      <th className="px-2 py-2 text-left text-[10px] uppercase text-neutral-500 font-semibold">Model</th>
                      <th className="px-2 py-2 text-right text-[10px] uppercase text-neutral-500 font-semibold">Adet</th>
                      <th className="px-2 py-2 text-right text-[10px] uppercase text-neutral-500 font-semibold">Toplam</th>
                    </>
                  )}
                  {tab === "salaries" && (
                    <>
                      <th className="px-2 py-2 text-left text-[10px] uppercase text-neutral-500 font-semibold">Görev</th>
                      <th className="px-2 py-2 text-right text-[10px] uppercase text-neutral-500 font-semibold">Ay Sayısı</th>
                      <th className="px-2 py-2 text-right text-[10px] uppercase text-neutral-500 font-semibold">Yıllık Toplam</th>
                    </>
                  )}
                  {MONTHS.map(m => <th key={m} className="px-1.5 py-2 text-right text-[10px] uppercase text-neutral-500 font-semibold num">{m}</th>)}
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 && (
                  <tr><td colSpan={20} className="text-center py-8 text-neutral-500">Kayıt bulunamadı</td></tr>
                )}
                {rows.map((r, i) => (
                  <tr key={i} className={`border-t border-[#F5F5F5] hover:bg-[#FAFAFA] ${tab === "companies" ? "cursor-pointer" : ""}`}
                      onClick={tab === "companies" ? () => openCompanyDetail(r) : undefined}
                      data-testid={tab === "companies" ? `company-row-${i}` : undefined}>
                    <td className="px-3 py-1.5 font-semibold">
                      {tab === "companies" ? (
                        <span className="hover:underline text-[#0020C2]">{r.name || r.plate}</span>
                      ) : (r.name || r.plate)}
                    </td>
                    {tab === "companies" && (
                      <>
                        <td className="px-2 py-1.5 text-right num text-[#0020C2] font-semibold">{formatTL(r.invoice_total)}</td>
                        <td className="px-2 py-1.5 text-right num text-[#16A34A] font-semibold">{formatTL(r.payment_total)}</td>
                        <td className={`px-2 py-1.5 text-right num font-bold ${(r.uncollected || 0) > 0.5 ? "text-[#DC2626] bg-[#FEE2E2]" : "text-neutral-400"}`}>{formatTL(r.uncollected || 0)}</td>
                        <td className={`px-2 py-1.5 text-right num font-bold ${r.balance > 0 ? "text-[#DC2626]" : "text-[#16A34A]"}`}>{formatTL(r.balance)}</td>
                      </>
                    )}
                    {tab === "suppliers" && (
                      <>
                        <td className="px-2 py-1.5 text-right num text-[#0020C2] font-semibold">{formatTL(r.invoice_total)}</td>
                        <td className="px-2 py-1.5 text-right num text-[#16A34A] font-semibold">{formatTL(r.payment_total)}</td>
                        <td className={`px-2 py-1.5 text-right num font-bold ${r.balance > 0 ? "text-[#DC2626]" : "text-[#16A34A]"}`}>{formatTL(r.balance)}</td>
                      </>
                    )}
                    {tab === "drivers" && (
                      <>
                        <td className="px-2 py-1.5 text-right num text-[#0020C2] font-semibold">{formatTL(r.earnings_total)}</td>
                        <td className="px-2 py-1.5 text-right num text-[#F59E0B] font-semibold">{formatTL(r.salary_total)}</td>
                        <td className={`px-2 py-1.5 text-right num font-bold ${r.net > 0 ? "text-[#16A34A]" : "text-[#DC2626]"}`}>{formatTL(r.net)}</td>
                      </>
                    )}
                    {tab === "fuel" && (
                      <>
                        <td className="px-2 py-1.5 text-neutral-600">{[r.brand, r.model].filter(Boolean).join(" ")}</td>
                        <td className="px-2 py-1.5 text-right num">{r.count}</td>
                        <td className="px-2 py-1.5 text-right num text-[#F59E0B] font-bold">{formatTL(r.total)}</td>
                      </>
                    )}
                    {tab === "salaries" && (
                      <>
                        <td className="px-2 py-1.5 text-neutral-600">{r.role || (r.type === "driver" ? "Şoför" : "Personel")}</td>
                        <td className="px-2 py-1.5 text-right num">{r.count}</td>
                        <td className="px-2 py-1.5 text-right num text-[#F59E0B] font-bold">{formatTL(r.total)}</td>
                      </>
                    )}
                    {(r.months || []).map((v, j) => (
                      <td key={j} className="px-1.5 py-1.5 text-right num text-[10px] text-neutral-600">
                        {v > 0 ? formatNumber(v) : "-"}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* Firma Detay Modal (Yıllık Konsolide → Firmalar tab) */}
      {detail && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setDetail(null)}>
          <div className="bg-white w-full max-w-6xl rounded-md shadow-lg max-h-[92vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-[#E5E5E5] sticky top-0 bg-white z-10">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-neutral-500">{year} Yılı Firma Detayı</div>
                <div className="font-heading font-bold text-lg" data-testid="detail-title">{detail.row.name}</div>
              </div>
              <button data-testid="detail-close" onClick={() => setDetail(null)} className="p-1.5 hover:bg-[#F5F5F5]">
                <X className="w-4 h-4" />
              </button>
            </div>

            {detail.loading && (
              <div className="p-8 text-center text-sm text-neutral-500">Yükleniyor...</div>
            )}

            {detail.data && (
              <div className="p-6 space-y-4">
                {/* Grand summary */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div className="border border-[#E5E5E5] p-3">
                    <div className="text-[10px] uppercase tracking-widest text-neutral-500">Kesilen Fatura</div>
                    <div className="num font-bold text-lg text-[#0020C2]">{formatTL(detail.data.grand.invoice_total)}</div>
                    <div className="text-[11px] text-neutral-500 num">{detail.data.grand.invoice_count} adet</div>
                  </div>
                  <div className="border border-[#E5E5E5] p-3">
                    <div className="text-[10px] uppercase tracking-widest text-neutral-500">Tahsilat</div>
                    <div className="num font-bold text-lg text-[#16A34A]">{formatTL(detail.data.grand.payment_total)}</div>
                    <div className="text-[11px] text-neutral-500 num">{detail.data.grand.payment_count} adet</div>
                  </div>
                  <div className="border border-[#E5E5E5] p-3">
                    <div className="text-[10px] uppercase tracking-widest text-neutral-500">Bakiye</div>
                    <div className={`num font-bold text-lg ${detail.data.grand.balance > 0 ? "text-[#DC2626]" : "text-[#16A34A]"}`}>{formatTL(detail.data.grand.balance)}</div>
                  </div>
                  <div className="border border-[#E5E5E5] p-3">
                    <div className="text-[10px] uppercase tracking-widest text-[#DC2626]">Tahsil Edilmemiş</div>
                    <div className={`num font-bold text-lg ${(detail.data.grand.uncollected || 0) > 0.5 ? "text-[#DC2626]" : "text-neutral-400"}`}>{formatTL(detail.data.grand.uncollected)}</div>
                  </div>
                </div>

                {/* Firma bilgileri */}
                {detail.data.company && (
                  <div className="border border-[#E5E5E5] rounded p-4 bg-[#FAFAFA]">
                    <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-2">Firma Bilgileri</div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-x-6 gap-y-1 text-xs">
                      <div><span className="text-neutral-500">VD:</span> {detail.data.company.tax_office || "-"}</div>
                      <div><span className="text-neutral-500">VD No:</span> <span className="num">{detail.data.company.tax_no || "-"}</span></div>
                      <div><span className="text-neutral-500">Vade:</span> {detail.data.company.payment_terms || "-"}</div>
                      <div><span className="text-neutral-500">Yetkili:</span> {detail.data.company.contact_person || "-"}</div>
                      <div><span className="text-neutral-500">Telefon:</span> <span className="num">{detail.data.company.phone || "-"}</span></div>
                      <div><span className="text-neutral-500">Email:</span> {detail.data.company.email || "-"}</div>
                    </div>
                  </div>
                )}

                {/* Aylık kırılım */}
                {detail.data.months.filter(m => m.invoice_count > 0 || m.payment_count > 0).map(m => (
                  <div key={m.month} className="border border-[#E5E5E5] rounded" data-testid={`detail-month-${m.month}`}>
                    <div className="flex items-center justify-between px-4 py-2.5 bg-[#FAFAFA] border-b border-[#E5E5E5]">
                      <div className="flex items-center gap-3">
                        <div className="font-semibold text-sm">{MONTHS[parseInt(m.month.slice(5,7)) - 1]} {m.month.slice(0,4)}</div>
                        <div className="text-[10px] uppercase tracking-widest text-neutral-500">
                          {m.invoice_count} fatura · {m.payment_count} tahsilat
                        </div>
                      </div>
                      <div className="flex items-center gap-4 text-xs">
                        <div className="num text-[#0020C2] font-semibold">{formatTL(m.invoice_total)}</div>
                        <div className="num text-[#16A34A] font-semibold">{formatTL(m.payment_total)}</div>
                        <div className={`num font-bold ${m.balance > 0 ? "text-[#DC2626]" : m.balance < 0 ? "text-[#16A34A]" : "text-neutral-500"}`}>
                          {formatTL(m.balance)}
                        </div>
                      </div>
                    </div>
                    <table className="w-full text-xs">
                      <thead className="bg-white border-b border-[#F5F5F5]">
                        <tr>
                          <th className="px-3 py-1.5 text-left text-[10px] uppercase tracking-widest text-neutral-500 font-semibold w-24">Tarih</th>
                          <th className="px-3 py-1.5 text-left text-[10px] uppercase tracking-widest text-neutral-500 font-semibold w-24">Tür</th>
                          <th className="px-3 py-1.5 text-left text-[10px] uppercase tracking-widest text-neutral-500 font-semibold">Referans / Açıklama</th>
                          <th className="px-3 py-1.5 text-right text-[10px] uppercase tracking-widest text-neutral-500 font-semibold w-32">Tutar (₺)</th>
                        </tr>
                      </thead>
                      <tbody>
                        {[
                          ...m.invoices.map(x => ({ ...x, _type: "inv" })),
                          ...m.payments.map(x => ({ ...x, _type: "pay" })),
                        ].sort((a,b) => (a.date || "").localeCompare(b.date || "")).map((r, i) => (
                          <tr key={i} className="border-t border-[#F5F5F5]">
                            <td className="px-3 py-1.5 num text-neutral-600">{r.date}</td>
                            <td className="px-3 py-1.5">
                              <span className={`text-[9px] uppercase tracking-widest font-semibold px-1.5 py-0.5 text-white ${r._type === "inv" ? "bg-[#0020C2]" : "bg-[#16A34A]"}`}>
                                {r._type === "inv" ? "Fatura" : "Tahsilat"}
                              </span>
                            </td>
                            <td className="px-3 py-1.5 text-neutral-700">
                              {r._type === "inv" ? (r.invoice_no || "-") : (r.payment_method || r.note || "-")}
                            </td>
                            <td className={`px-3 py-1.5 num text-right font-semibold ${r._type === "inv" ? "text-[#0020C2]" : "text-[#16A34A]"}`}>
                              {formatTL(r.amount)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ))}

                {detail.data.months.every(m => m.invoice_count === 0 && m.payment_count === 0) && (
                  <div className="text-center py-8 text-sm text-neutral-500">Bu yıla ait fatura veya tahsilat kaydı bulunamadı.</div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
